import { useEffect, useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ExamLabelPicker, { type ExamLabel } from "./ExamLabelPicker";
import NoteAIAssistant from "./NoteAIAssistant";
import RichTextEditor from "./RichTextEditor";
import { QuickActionToolbar } from "./QuickActionToolbar";
import { toast } from "@/hooks/use-toast";

interface NoteFormProps {
  topics: { id: string; name: string }[];
  initial?: { title?: string; content?: string; topic_id?: string | null; estimated?: number };
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (payload: { title: string; content: string; topic_id: string | null; estimated: number }) => void;
  onCancel?: () => void;
  examLabels?: ExamLabel[];
  examLabelIds?: string[];
  onLabelsChange?: (ids: string[]) => void;
}

const NoteForm = ({ topics, initial, submitting, submitLabel = "Save", onSubmit, onCancel, examLabels, examLabelIds = [], onLabelsChange }: NoteFormProps) => {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [content, setContent] = useState(initial?.content ?? "");
  const [topicId, setTopicId] = useState<string | "" | null>(initial?.topic_id ?? "");
  const [estimated, setEstimated] = useState<number>(initial?.estimated ?? 5);
  const assistantRef = useRef<{ triggerAction: (action: string) => void }>(null);

  useEffect(() => {
    setTitle(initial?.title ?? "");
    setContent(initial?.content ?? "");
    setTopicId(initial?.topic_id ?? "");
    setEstimated(initial?.estimated ?? 5);
  }, [initial]);

  const selectedTopic = topics.find(t => t.id === topicId);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: title.trim(),
          content: content.trim(),
          topic_id: topicId ? String(topicId) : null,
          estimated: Number(estimated) || 5,
        });
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form Fields */}
        <div className="space-y-4">
          <div className="grid gap-2">
            <label className="text-sm font-medium">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          
          <div className="grid gap-2">
            <label className="text-sm font-medium">Topic</label>
            <select
              className="rounded-md border bg-background px-3 py-2"
              value={topicId ?? ""}
              onChange={(e) => setTopicId(e.target.value || "")}
            >
              <option value="">No topic</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="grid gap-2">
            <label className="text-sm font-medium">Estimated minutes</label>
            <Input type="number" min={1} max={60} value={estimated} onChange={(e) => setEstimated(Number(e.target.value))} />
          </div>
          
          <div className="grid gap-2">
            <label className="text-sm font-medium">Content</label>
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder="Start typing your medical note..."
              onPasteDetected={(pastedContent) => {
                toast({
                  title: "Content pasted! 📋",
                  description: "Scroll down to the AI Assistant to use Smart Revision for automatic review.",
                  duration: 5000,
                });
              }}
            />
          </div>
          
          {examLabels && onLabelsChange && (
            <ExamLabelPicker labels={examLabels} value={examLabelIds ?? []} onChange={onLabelsChange} />
          )}
          
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : submitLabel}
            </Button>
            {onCancel && (
              <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Right Column - AI Assistant with Quick Toolbar */}
        <div className="space-y-4">
          <QuickActionToolbar
            onSmartRevision={() => assistantRef.current?.triggerAction('smart_revision')}
            onContentHealth={() => assistantRef.current?.triggerAction('content_health')}
            onQuickFormat={() => {
              // Quick format implementation
              toast({
                title: "Quick Format",
                description: "Use the Auto-Formatting tools in the AI Assistant section",
              });
            }}
            onGetSuggestions={() => assistantRef.current?.triggerAction('suggest_terms')}
          />

          <NoteAIAssistant
            ref={assistantRef}
            content={content}
            title={title}
            topic={selectedTopic?.name}
            onContentUpdate={setContent}
          />
        </div>
      </div>
    </form>
  );
};

export default NoteForm;
